import type { ListAPInvoicesResponse } from '@erp/rpc';
import {
  AP_INVOICE_BASE_COLUMNS,
  AP_INVOICES_TABLE_ID,
  type APInvoiceBaseColumnId,
  type ColumnConfigEntry,
  type ExtensionColumnMetadata,
} from '@erp/shared-types';
import { type ColumnDef, flexRender } from '@/lib/tanstack-table';
import { DataTableColumnHeader } from '@/components/common/data-table/data-table-column-header';
import type { FieldOption } from '@/components/common/ColumnConfigDialog';
import { ExtensionColumnCell } from '@/components/common/schema-extensions/ExtensionColumnCell';
import { ExtensionColumnIssues } from '@/components/common/schema-extensions/ExtensionColumnIssues';
import { extensionColumnMetadata } from '@/components/common/schema-extensions/extensionColumns';
import { useColumnConfig } from '@/hooks/useColumnConfig';

/** A list row: the invoice plus the extension values its columns requested. */
export type InvoiceListRow = ListAPInvoicesResponse['invoices'][number];

type Translate = (str: string, opts?: Record<string, unknown>) => string;

export function invoiceDefaultColumns(gt: Translate): ColumnConfigEntry[] {
  const headers: Record<APInvoiceBaseColumnId, string> = {
    createdAt: gt('Imported', {
      context: 'column header for the date an invoice was imported',
    }),
    receivedAt: gt('Received', {
      context:
        'column header for when the invoice was received (email inbox time)',
    }),
    invoiceNumber: gt('Invoice #', {
      context: 'column header for the invoice number',
    }),
    invoiceDate: gt('Invoice Date', {
      context: 'column header for the invoice date',
    }),
    dueDate: gt('Due Date', {
      context: 'column header for the invoice due date',
    }),
    totalAmount: gt('Total', {
      context: 'column header for the invoice total amount',
    }),
    supplierId: gt('Vendor', {
      context: 'column header for the invoice vendor/supplier',
    }),
    referenceIds: gt('Reference', {
      context: 'column header for the invoice reference number(s)',
    }),
    status: gt('Status', {
      context: 'column header for the invoice status',
    }),
    paymentStatus: gt('Payment Status', {
      context: 'column header for the invoice payment status',
    }),
    noteCount: gt('Notes', {
      context:
        'column header for the indicator showing whether people left notes on an invoice',
    }),
  };
  return AP_INVOICE_BASE_COLUMNS.map((column) => ({
    ...column,
    header: headers[column.id],
  }));
}

export function useInvoiceColumnConfig(gt: Translate) {
  const defaults = invoiceDefaultColumns(gt);
  return {
    ...useColumnConfig(AP_INVOICES_TABLE_ID, defaults, {
      savedColumns: 'addNewDefaults',
    }),
    defaults,
  };
}

export function invoiceColumnFields(
  defaults: ColumnConfigEntry[],
  configured: ColumnConfigEntry[],
): FieldOption[] {
  const configuredFields = new Set(configured.map((column) => column.apiField));
  return defaults
    .filter((column) => !configuredFields.has(column.apiField))
    .map((column) => ({
      apiField: column.apiField,
      label: column.header,
      format: column.format ?? 'string',
    }));
}

/** Preserve AP cells and widths while applying saved membership and presentation. */
export function configureInvoiceColumns(
  baseColumns: ColumnDef<InvoiceListRow>[],
  configured: ColumnConfigEntry[],
  extensionMetadata?: ExtensionColumnMetadata[],
): ColumnDef<InvoiceListRow>[] {
  return configured.flatMap((config) => {
    if (config.hidden) return [];
    if (config.source?.kind === 'extension') {
      return [
        extensionInvoiceColumn(
          config,
          extensionColumnMetadata(extensionMetadata, config.source),
        ),
      ];
    }
    const base = baseColumns.find(
      (column) =>
        'accessorKey' in column && column.accessorKey === config.apiField,
    );
    if (!base) return [];
    return [
      {
        ...base,
        id: config.id,
        enableSorting:
          base.enableSorting !== false && config.sortable !== false,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={config.header} />
        ),
        cell: config.alignRight
          ? (context) => (
              <div className="flex w-full justify-end">
                {flexRender(base.cell, context)}
              </div>
            )
          : base.cell,
      },
    ];
  });
}

/** Extension values are projection-only: never sortable or filterable. */
function extensionInvoiceColumn(
  config: ColumnConfigEntry,
  metadata: ExtensionColumnMetadata | undefined,
): ColumnDef<InvoiceListRow> {
  return {
    id: config.id,
    size: 140,
    enableSorting: false,
    enableColumnFilter: false,
    header: ({ column }) => (
      <div className="flex items-center gap-1">
        <DataTableColumnHeader column={column} title={config.header} />
        <ExtensionColumnIssues metadata={metadata} />
      </div>
    ),
    cell: ({ row }) => (
      <div className={config.alignRight ? 'flex w-full justify-end' : ''}>
        <ExtensionColumnCell
          metadata={metadata}
          ext={row.original.ext}
          targetModel="AP_INVOICE"
          entityKey={row.original.id}
          currency={row.original.currency || undefined}
        />
      </div>
    ),
  };
}

/** URL sorting stays on API fields even when a user adds a new column instance. */
export function invoiceSortColumnId(
  columns: ColumnConfigEntry[],
  apiField: string,
): string {
  return (
    columns.find((column) => !column.hidden && column.apiField === apiField)
      ?.id ?? apiField
  );
}

export function invoiceSortApiField(
  columns: ColumnConfigEntry[],
  columnId: string | undefined,
): string | undefined {
  return columns.find((column) => column.id === columnId)?.apiField;
}

export function invoiceOrgDefaultSaveHandler(
  columns: ColumnConfigEntry[],
  staffAdmin: boolean,
  canUpdateOrgConfig: boolean,
  save: (input: { tableId: string; columns: ColumnConfigEntry[] }) => void,
): (() => void) | undefined {
  if (!staffAdmin && !canUpdateOrgConfig) return undefined;
  return () => save({ tableId: AP_INVOICES_TABLE_ID, columns });
}
